import { RefObject } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VariableChipsMemo } from './VariableChips';
import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_CHANNELS,
  TEMPLATE_VALIDATION,
} from '@/constants/templateConstants';
import type { ITemplateFormState } from '@/@types/messageTemplates';
import type { TemplateCategory, TemplateChannel } from '@/constants/templateConstants';

interface ITemplateFormFieldsProps {
  formState: ITemplateFormState;
  setFormState: React.Dispatch<React.SetStateAction<ITemplateFormState>>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  onVariableClick: (variable: string) => void;
}

export function TemplateFormFields({
  formState,
  setFormState,
  textareaRef,
  onVariableClick,
}: ITemplateFormFieldsProps) {
  const handleChannelToggle = (channel: TemplateChannel, checked: boolean) => {
    setFormState(prev => ({
      ...prev,
      channels: checked ? [...prev.channels, channel] : prev.channels.filter(c => c !== channel),
    }));
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">Template Name</Label>
        <Input
          id="name"
          value={formState.name}
          onChange={e => setFormState(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Property Follow-up"
          className="bg-background-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Variables</Label>
        <div className="p-3 bg-muted/50 rounded-lg border border-border">
          <VariableChipsMemo onVariableClick={onVariableClick} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Message</Label>
        <Textarea
          ref={textareaRef}
          id="content"
          value={formState.content}
          onChange={e => setFormState(prev => ({ ...prev, content: e.target.value }))}
          placeholder="Write your message template here. Use variables like {{lead_first_name}} to personalize."
          className="min-h-[150px] bg-background-input"
        />
        <p className="text-xs text-muted-foreground text-right">
          {formState.content.length} / {TEMPLATE_VALIDATION.CONTENT_MAX_LENGTH}
        </p>
      </div>

      <div className="space-y-4">
        <Label>Settings</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="category" className="text-sm text-muted-foreground">
              Category
            </Label>
            <Select
              value={formState.category}
              onValueChange={value =>
                setFormState(prev => ({ ...prev, category: value as TemplateCategory }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Channels</Label>
            <div className="flex flex-wrap gap-3">
              {TEMPLATE_CHANNELS.map(channel => (
                <label key={channel.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={formState.channels.includes(channel.value)}
                    onCheckedChange={checked => handleChannelToggle(channel.value, !!checked)}
                  />
                  <span className="text-sm">{channel.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
